import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import type { AccordionItemProps } from '../AccordionComponent/AccordionItem';
import { AccordionItem } from '../AccordionComponent/AccordionItem';
import { List } from '../List/List';
import type { IdType } from '../Utils/typeUtils';
import styles from './AccordionSection.module.css';
import type { AccordionSectionLayout } from './AccordionSectionLayout';

export const AccordionSectionVariants = ['light', 'dark', 'gray'] as const;
export type AccordionSectionVariant = (typeof AccordionSectionVariants)[number];

interface AccordionSectionProps<T> {
    items: ReadonlyArray<T>;
    renderAccordionHeading: (item: T) => ReactNode;
    renderAccordionBody: (item: T) => ReactNode;
    title?: string;
    variant?: AccordionSectionVariant;
    layout?: AccordionSectionLayout;
    initialFirstExpanded?: boolean;
    itemOptions?: Pick<AccordionItemProps, 'color' | 'variant' | 'contentSpacing'>;
    spacing?: 'default' | 'none';
}

export const AccordionSection = <T extends { id: IdType }>(props: AccordionSectionProps<T>) => {
    const {
        items,
        renderAccordionHeading,
        renderAccordionBody,
        title,
        initialFirstExpanded,
        variant = 'light',
        layout = 'default',
        spacing = 'default',
        itemOptions = { color: 'dark', contentSpacing: 'none' },
    } = props;

    return (
        <div
            className={classNames(
                styles[`Variant--${variant}`],
                styles[`Variant--${layout}`],
                spacing !== 'none' && styles[`Variant--${spacing}Spacing`]
            )}
        >
            <div className={styles.Inner}>
                <div className={styles.Content}>
                    {title && <h2 className={styles.Title}>{title}</h2>}
                    <List
                        className={styles.List}
                        items={items}
                        renderItem={(item, index) => (
                            <li key={index}>
                                <AccordionItem
                                    headerContent={
                                        <h3 className={styles.AccordionTitle}>{renderAccordionHeading(item)}</h3>
                                    }
                                    initialExpanded={initialFirstExpanded && index === 0}
                                    {...itemOptions}
                                >
                                    <div className={styles.AccordionBody}>{renderAccordionBody(item)}</div>
                                </AccordionItem>
                            </li>
                        )}
                    />
                </div>
            </div>
        </div>
    );
};
